// The deployed program as this build knows it (docs/08-app-design.md 5.10
// and section 7, phishing): the published artifact's length and hash from
// docs/evidence/mainnet/2026-09-24/deploy-receipt.json, and the read that
// compares them with what the configured cluster actually runs. Changing
// what is deployed means recording a new receipt there and updating these
// two constants in the same commit. Used by the Verify page (to show) and
// by the engine (to refuse to sign against a program that differs).
//
// 2026-09-27: these two constants describe the fee-side artifact (the
// protocol fee taken from a USDC or USDT side, else a SOL side, else the
// output; program/src/fee.rs `fee_side`; with R63, a native output synced
// after every settled swap), built from this tree as
// target/deploy/thevault.so, 65,504 bytes, which the rpc shim loads. Mainnet
// still runs the 2026-09-24 artifact (64,112 bytes, sha256 82b4330d...2ea3,
// deploy-receipt.json) until the owner upgrades it, so the hash gate of a
// build carrying these values refuses to sign against mainnet today: the
// live app must be redeployed only after the mainnet program upgrade has
// landed and its receipt is recorded. (The app's swap figures also follow
// the new fee rule, which the old program does not enforce.)
//
// 2026-09-28: mainnet runs this artifact since slot 451,338,716
// (docs/evidence/mainnet/2026-09-28/upgrade-receipt.json). ProgramData was
// extended to 74,352 bytes, so the hash covers the first 65,504 and the
// rest must be zero, which `deployedProgramHash` checks.
//
// 2026-10-10: these two constants describe the staking artifact (native
// delegation from the vault, docs/19-staking-plan.md section 4.4; the ST7
// upgrade of docs/21-staking-mainnet-upgrade.md), built from this tree as
// target/deploy/thevault.so, 74,144 bytes, which the rpc shim loads and
// which devnet has run since 2026-10-05. This commit is deployed only in
// section 9 of docs/21, after the mainnet upgrade has landed and read back
// (section 8); a build carrying these values refuses to sign against the
// 2026-09-28 program. ProgramData grows to 84,592 bytes with the 10,240-byte
// extension of section 4, so the hash covers the first 74,144 and the rest
// must be zero. The receipt goes to docs/evidence/mainnet/<date>/
// upgrade-receipt.json (the 2026-09-28 schema) with the deploy record.

import { UPGRADEABLE_LOADER_ADDRESS } from '@thevault/sdk/canary';
import { type Rpc, toBase58 } from './rpc';

export const PUBLISHED_ARTIFACT_LEN = 74_144;
export const PUBLISHED_SHA256 = '3bb2f3a8a8cd55be7bf1db8063db0deb5b71b6552715e5a36ba0035294dec870';

export interface DeployedHash {
  /** False when the program account is not a `Program` (tag 2) owned by the upgradeable loader, the only layout the hash can be read from. */
  applies: boolean;
  programOwner: string | null;
  programDataAddress: string | null;
  sha256: string | null;
  tailIsZero: boolean | null;
  error: string | null;
  /** True only when the hash equals the published one and nothing sits beyond the artifact. */
  matches: boolean;
}

function hex(b: Uint8Array): string {
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

/** Read the program account, follow it to ProgramData, hash the first artifact-length bytes of the ELF region and check the rest is zero. Never throws; failures are reported in `error`. */
export async function deployedProgramHash(rpc: Rpc, programIdB58: string): Promise<DeployedHash> {
  const out: DeployedHash = { applies: false, programOwner: null, programDataAddress: null, sha256: null, tailIsZero: null, error: null, matches: false };
  try {
    const account = await rpc.getAccountInfo(programIdB58);
    if (!account) {
      out.error = 'The program id does not exist on this cluster.';
      return out;
    }
    out.programOwner = toBase58(account.owner);
    const tag = account.data.length >= 4 ? new DataView(account.data.buffer, account.data.byteOffset, account.data.byteLength).getUint32(0, true) : -1;
    out.applies = out.programOwner === UPGRADEABLE_LOADER_ADDRESS && tag === 2;
    if (!out.applies) return out;
    out.programDataAddress = toBase58(account.data.slice(4, 36));
    const pd = await rpc.getAccountInfo(out.programDataAddress);
    if (!pd) {
      out.error = 'The ProgramData account was not found.';
      return out;
    }
    const elf = pd.data.slice(45);
    if (elf.length < PUBLISHED_ARTIFACT_LEN) {
      out.error = `The ProgramData ELF region is ${elf.length} bytes, shorter than the published artifact (${PUBLISHED_ARTIFACT_LEN}).`;
      return out;
    }
    out.sha256 = hex(new Uint8Array(await crypto.subtle.digest('SHA-256', elf.slice(0, PUBLISHED_ARTIFACT_LEN))));
    out.tailIsZero = elf.slice(PUBLISHED_ARTIFACT_LEN).every((b) => b === 0);
    out.matches = out.sha256 === PUBLISHED_SHA256 && out.tailIsZero;
  } catch (e) {
    out.error = e instanceof Error ? e.message : String(e);
  }
  return out;
}

// ---------------------------------------------------------------- staking gates
//
// docs/19-staking-plan.md 6.3 item 8: staking signs only against programs this
// build was tested with. Three pins, none of which replaces the vault hash gate
// above (that one still decides whether anything signs at all):
//
// - the vault program must be a build that has the staking rules (docs/19 4.4):
//   the mainnet artifact refuses every staking payload with Custom(11), after
//   the key is used, so without this pin a stake from today's mainnet program
//   would retire a key for nothing. The one such build is the ST2 artifact
//   (74,144 bytes, sha256 3bb2f3a8..., docs/02-build-plan.md M2.12), the
//   published artifact since the ST7 upgrade (PUBLISHED_SHA256 above is the
//   same hash, 2026-10-10); a later build with the staking rules is added here
//   in the commit that publishes it.
// - the Stake program's ELF (Stake program@v5.1.0, sdk/fixtures/stake/README.md)
//   and the Token program's ELF (p-token v1.0.0,
//   docs/evidence/staking/2026-10-05/stake-program/token-programdata-meta.json):
//   both change only by feature-gated upgrades, and the staking UI turns off
//   until a new version has been replayed.

/** A vault program build that carries the staking rules. */
export interface ArtifactPin {
  len: number;
  sha256: string;
  label: string;
}

export const STAKING_ARTIFACTS: ArtifactPin[] = [{ len: 74_144, sha256: '3bb2f3a8a8cd55be7bf1db8063db0deb5b71b6552715e5a36ba0035294dec870', label: 'the staking build (ST2; published by the ST7 upgrade, docs/21)' }];

/** sha256 of the Stake program's whole ELF region (ProgramData bytes 45..end), Stake program@v5.1.0. */
export const STAKE_ELF_SHA256 = '3d2d39c596ce8be2d47816b4ee5db9fc759d80fde54b08c930ad0b6daed64c2c';
/** sha256 of the Token program's whole ELF region (ProgramData bytes 45..end), p-token v1.0.0. */
export const TOKEN_ELF_SHA256 = '8190d3f7ceb6cb7a7a8d8924bff89f9f611e15ce1f806f2b6237f3311a98f697';

/** The ELF region of an upgradeable-loader program (its ProgramData bytes 45..end), or why it could not be read. Never throws. */
export async function programElf(rpc: Rpc, programIdB58: string): Promise<{ elf: Uint8Array | null; error: string | null }> {
  try {
    const account = await rpc.getAccountInfo(programIdB58);
    if (!account) return { elf: null, error: 'the program account does not exist on this cluster' };
    const tag = account.data.length >= 4 ? new DataView(account.data.buffer, account.data.byteOffset, account.data.byteLength).getUint32(0, true) : -1;
    if (toBase58(account.owner) !== UPGRADEABLE_LOADER_ADDRESS || tag !== 2 || account.data.length < 36) return { elf: null, error: 'the program is not in the upgradeable loader layout' };
    const pd = await rpc.getAccountInfo(toBase58(account.data.slice(4, 36)));
    if (!pd || pd.data.length <= 45) return { elf: null, error: 'its ProgramData account was not found' };
    return { elf: pd.data.slice(45), error: null };
  } catch (e) {
    return { elf: null, error: e instanceof Error ? e.message : String(e) };
  }
}

/** sha256 (hex) of a whole ELF region, as the pins above are taken. */
export async function elfSha256(elf: Uint8Array): Promise<string> {
  return hex(new Uint8Array(await crypto.subtle.digest('SHA-256', new Uint8Array(elf))));
}

/** The pin among `pins` whose first `len` bytes hash to its sha256 with only zeros after them (the deployedProgramHash rule), or null. */
export async function matchingArtifact(elf: Uint8Array, pins: ArtifactPin[]): Promise<ArtifactPin | null> {
  for (const pin of pins) {
    if (elf.length < pin.len) continue;
    if (!elf.slice(pin.len).every((b) => b === 0)) continue;
    if ((await elfSha256(elf.slice(0, pin.len))) === pin.sha256) return pin;
  }
  return null;
}
